import type { DrawContext } from './draw';
import type { LabelSpace } from './labels';
import type { MapNode, ShownNode } from './nodes';

/*
 * THE NAMES, WRITTEN — the last layer of `drawStatic`.
 *
 * Ported from `reference/universe-v8/engine.js` — lines 1672–1713.
 *
 * Everything before this draws POSITION. This is the only layer that says what anything is called,
 * and it is the most expensive thing on the screen: text costs a measure and a raster per name, and
 * there are ten thousand names. So it is fenced in six different ways, and every fence is doing work.
 *
 *   the zoom       nothing is written until the reader is close enough to read it
 *   the speed      nothing is written while the camera is flying — nobody is reading
 *   the budget     at most a few dozen a frame, more as the reader comes in
 *   the weight     minor names wait until the reader is closer still
 *   the fog        a name they have never been near is not named
 *   the room       the chrome, other labels, and every DISC on the screen
 *
 * THAT LAST ONE IS THE ONE WORTH POINTING AT. A label may not sit on top of another name's disc,
 * even a disc whose own label is not being drawn — because a word lying across a dot makes the dot
 * look like part of the word. It is the difference between a map and a mess, and it costs a check
 * against every glyph on screen for every label placed.
 *
 * AND THE ORDER IS BY WEIGHT, HEAVIEST FIRST, which is what makes the budget mean something: the
 * few dozen names that get written are the few dozen most written-about, not the first few dozen in
 * the roster. (Theirs sorts `shown` IN PLACE, so the list the picker later reads is in weight order
 * rather than the depth order it was painted in. Ours does the same, on purpose — see `sortByWeight`.)
 */

/** How fast the camera may be moving and still have names written. Screen pixels a second. */
const STILL_ENOUGH = 40;

/** How many names one frame may write, by zoom band, and while the whole field is revealed. */
const BUDGET = [16, 24, 32];
const BUDGET_REVEALED = 90;

/** The occupancy grid a label reserves cells in: a coarse second pass over the fine one. */
const CELL_W = 26;
const CELL_H = 26;
const CELL_PAD = 14;

/** A disc must be this big, and this far out of the fog, before a label may not sit on it. */
const GLYPH_MIN_R = 3.2;
const GLYPH_MIN_FOG = 0.18;
/** And this much air around it. */
const GLYPH_PAD = 3;

/** How far out of the fog a name must be to be written at all, and to ignore the zoom floor. */
const FOG_TO_NAME = 0.3;
const FOG_CERTAIN = 0.9;

/** The font. Look. */
const FONT_BASE = 8.5;
const FONT_MIN = 10;
const FONT_MAX = 19;
/** The label is set one and a half pixels larger than the size the box is measured at. */
const FONT_BUMP = 1.5;

/** How far right of a name its label sits, and the extra room the open one gets. */
const LABEL_GAP = 6;
const SELECTED_PAD = 42;

/** How close to the screen edge a label may sit. */
const EDGE = 10;

/** How tall the label's box is, as a share of the font size either side. */
const BOX_HALF = 0.6;

/** How opaque a label is, sure and unsure. */
const ALPHA_VERIFIED = 0.9;
const ALPHA_UNVERIFIED = 0.55;

/** The baseline nudge that centres the text on the disc. */
const BASELINE_NUDGE = 0.5;

/** Below this much fog left, the arrival is still showing the field and the gates are relaxed. */
const REVEAL_UNTIL = 0.55;

/**
 * THE ZOOMS THIS LAYER STEPS AT — all arguments, because a zoom is pixels per world unit.
 *
 * Seven of them, which makes eighteen lifted out of this engine so far. A copied `0.45` on a world
 * ten times the size would hold every minor name back past any camera a reader ever uses.
 */
export interface LabelZooms {
  /** Below this, no names at all. */
  from: number;
  /** Where the budget steps up, twice. */
  budgetSteps: readonly [number, number];
  /** Where the weight gate steps down, four times. */
  weightSteps: readonly [number, number, number, number];
  /** And once, during the arrival's reveal. */
  revealStep: number;
  /** Above this the weight gate is ignored: close in, everything with room gets its name. */
  ignoreWeightAbove: number;
}

/**
 * THE WEIGHT LADDER — how important a name must be to be written, by zoom band.
 *
 * Data twice over: the ladder is measured against OUR weights, and where it steps is measured
 * against OUR world. Theirs runs 7, 5, 4, 3, 0 over five bands against a scale topping out at 10.
 */
export interface LabelWeights {
  /** The gate in each of the five bands, outermost first. */
  steps: readonly [number, number, number, number, number];
  /** The two gates in force while the arrival is showing the whole field. */
  reveal: readonly [number, number];
  /** At and above this a name is set in the heavier face. */
  bold: number;
  /** How much a name's weight and the zoom each add to its font size. */
  perWeight: number;
  perZoom: number;
}

/**
 * THEIRS, AS THE ENGINE STATES THEM — engine.js:1676 for the ladder, 1660-1690 for the rest.
 *
 * Exported because two callers need them and neither may hold its own copy. The test beside this
 * checks the mechanism against the engine using these; `our-scene.ts` rescales them to our world.
 * They lived only in the test, and the wiring beside it was written from nothing: `from` was 0.09
 * where theirs is 0.3, and the ladder ran 1,2,3,4,5 — ASCENDING, where theirs descends. That is the
 * gate that decides how much text is on the far view, and inverted it wrote a name for anything with
 * two ties at the whole-field zoom, which is the wall of names their map does not have.
 */
export const THEIR_LABEL_ZOOMS: LabelZooms = {
  from: 0.3,
  budgetSteps: [0.5, 1],
  weightSteps: [0.45, 0.7, 1.1, 1.9],
  revealStep: 0.2,
  ignoreWeightAbove: 0.9,
};

/**
 * Their weight ladder. STRICTEST FAR OUT: seven ties to earn a name across the whole field, falling
 * to none once the reader is close enough that there is room for everything.
 */
export const THEIR_LABEL_WEIGHTS: LabelWeights = {
  steps: [7, 5, 4, 3, 0],
  reveal: [5, 2],
  bold: 7,
  perWeight: 0.75,
  perZoom: 1.2,
};

/** The roster the ladder above was measured against — what a weight of 7 was 7 out of. */
export const THEIR_TOP_WEIGHT = 10;

/**
 * Their ladder read against OUR roster.
 *
 * A weight is a count of ties, so it means nothing without the scale it is counted on: seven out of
 * their ten is most of the way up, and seven out of our forty is barely off the floor. Everything
 * that is a weight scales; everything that is a zoom does not, and is rescaled by the rim elsewhere.
 *
 * `perWeight` scales the other way — it turns a weight into font pixels, so a taller scale must add
 * less per step or the largest names would be set at twice the size theirs are.
 */
export function labelWeightsFor(topWeight: number): LabelWeights {
  const scale = Math.max(1, topWeight) / THEIR_TOP_WEIGHT;
  const [a, b, c, d, e] = THEIR_LABEL_WEIGHTS.steps;
  const [r0, r1] = THEIR_LABEL_WEIGHTS.reveal;
  return {
    steps: [a * scale, b * scale, c * scale, d * scale, e * scale],
    reveal: [r0 * scale, r1 * scale],
    bold: THEIR_LABEL_WEIGHTS.bold * scale,
    perWeight: THEIR_LABEL_WEIGHTS.perWeight / scale,
    perZoom: THEIR_LABEL_WEIGHTS.perZoom,
  };
}

/** What the layer needs to know about the reader and the moment. */
export interface LabelState {
  /** How fast the camera is moving, in screen pixels a second. */
  speed: number;
  /** The reader's own preference for how many names they want. */
  density: number;
  /**
   * WHETHER THE ARRIVAL IS STILL RUNNING, and how far the fog has closed back in.
   *
   * This was `revealAll`, read straight off the frame's fog — and the frame hard-codes that TRUE,
   * for a reason that is right and is about something else: a reader who has walked nowhere has
   * earned the sight of the whole field, so the fog shows everything. The budget below read the
   * same flag to mean "the survey is holding the field open, write up to ninety names", and so
   * every reader who had walked nowhere got the survey's budget for as long as they stayed.
   *
   * Measured at the widest view: 78 names written where the band allows 16. Theirs writes none
   * there at all. One flag, two quantities — so the label layer now asks its own question.
   */
  surveying: boolean;
  fogMix: number;
  /** The phase, because only on the map is a name held back for being out of reach. */
  phase: string;
  /** The zoom floor, from `minZoom` — a name below it is only written if it is certain. */
  floor: number;
  /** Which name the reader has open, which gets room for its own panel. */
  focus: number | null;
  /** Whether a name is somewhere the reader could walk to from where they have been. */
  reachable: (node: MapNode) => boolean;
  /**
   * WHETHER A NAME OUT OF REACH IS STILL WRITTEN.
   *
   * Theirs does not write one: on the map, a figure the reader could not walk to is left as a light
   * with no name — "an unnamed light · not yet surveyed" — and that is its frontier.
   *
   * Ours names everything it draws, decided and recorded in `v8/hover.ts`, where the same question
   * is standing open with Ariel for the hover card. Both surfaces have to answer it the same way, so
   * it is one argument here rather than a second decision.
   *
   * It is an ARGUMENT because it was an ACCIDENT. The gate below is theirs, ported faithfully, and
   * it never fired: it is skipped while the survey is revealing, and the frame declared the survey
   * permanently on. So our map named everything for the right reason by the wrong mechanism — and
   * the same mechanism held the label budget at the survey's ninety for every reader who had walked
   * nowhere. Fixing the budget turned this gate on for the first time and the map went silent: nought
   * names at every zoom, because a reader who has walked nowhere can reach nothing.
   */
  namesOutOfReach: boolean;
  /** Set by the caller to skip the layer entirely — theirs is `_skipLabels`, used by the profiler. */
  skip?: boolean;
}

/** Whether this frame writes any names at all. */
export function labelsShowing(zoom: number, state: LabelState, zooms: LabelZooms): boolean {
  return zoom > zooms.from && state.speed < STILL_ENOUGH && state.skip !== true;
}

/** Whether the arrival is still holding the field open, which relaxes the budget and the gate. */
export function revealing(state: LabelState): boolean {
  return state.surveying && state.fogMix < REVEAL_UNTIL;
}

/** How many names this frame may write. */
export function labelBudget(zoom: number, state: LabelState, zooms: LabelZooms): number {
  if (revealing(state)) return BUDGET_REVEALED;
  const band = zoom < zooms.budgetSteps[0] ? 0 : zoom < zooms.budgetSteps[1] ? 1 : 2;
  return Math.round(state.density * (BUDGET[band] ?? 0));
}

/** How heavy a name must be to be written at this zoom. */
export function labelMinWeight(zoom: number, state: LabelState, zooms: LabelZooms, weights: LabelWeights): number {
  if (revealing(state)) return zoom < zooms.revealStep ? weights.reveal[0] : weights.reveal[1];
  const [a, b, c, d] = zooms.weightSteps;
  if (zoom < a) return weights.steps[0];
  if (zoom < b) return weights.steps[1];
  if (zoom < c) return weights.steps[2];
  if (zoom < d) return weights.steps[3];
  return weights.steps[4];
}

/** How big a name is set: its own weight, plus a little for how close the reader is. */
export function labelFontSize(node: MapNode, zoom: number, weights: LabelWeights): number {
  return Math.max(FONT_MIN, Math.min(FONT_MAX, FONT_BASE + node.w * weights.perWeight + zoom * weights.perZoom));
}

/**
 * Heaviest first, IN PLACE.
 *
 * Theirs sorts the same array it hands to the picker, so the picker sees weight order. Kept, because
 * a picker walking the list in weight order resolves an overlap toward the more written-about name,
 * which is almost always the one a reader meant.
 */
export function sortByWeight(shown: ShownNode[]): ShownNode[] {
  shown.sort((a, b) => b.node.w - a.node.w);
  return shown;
}

/** A disc a label may not lie across. */
interface Glyph {
  id: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Every disc big enough and clear enough of the fog to be worth protecting. */
function glyphsOf(shown: readonly ShownNode[]): Glyph[] {
  const glyphs: Glyph[] = [];
  for (const s of shown) {
    if (s.r <= GLYPH_MIN_R || s.fog <= GLYPH_MIN_FOG) continue;
    const q = (s.hit || s.r) + GLYPH_PAD;
    glyphs.push({ id: s.node.id, x0: s.sx - q, y0: s.sy - q, x1: s.sx + q, y1: s.sy + q });
  }
  return glyphs;
}

/** Whether a box lies across any disc but its own. */
function overGlyph(glyphs: readonly Glyph[], id: number, x0: number, y0: number, x1: number, y1: number): boolean {
  for (const g of glyphs) {
    if (g.id !== id && x0 < g.x1 && x1 > g.x0 && y0 < g.y1 && y1 > g.y0) return true;
  }
  return false;
}

/**
 * Write the names. Returns how many were written.
 *
 * `shown` is SORTED IN PLACE, which is theirs and is deliberate — see `sortByWeight`.
 */
export function drawNodeLabels(
  ctx: DrawContext,
  shown: ShownNode[],
  viewport: { width: number; height: number },
  zoom: number,
  state: LabelState,
  zooms: LabelZooms,
  weights: LabelWeights,
  space: LabelSpace,
): number {
  if (!labelsShowing(zoom, state, zooms)) return 0;
  const reveal = revealing(state);
  const budget = labelBudget(zoom, state, zooms);
  const minWeight = labelMinWeight(zoom, state, zooms, weights);
  const W = viewport.width;
  /* The occupancy grid: a coarse row-and-column reservation on top of the exact box test, which is
     what stops a row of labels crowding into a stripe even when no two of them quite touch. */
  const occupied = new Set<string>();
  const glyphs = glyphsOf(shown);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  sortByWeight(shown);
  let placed = 0;

  for (let i = 0; i < shown.length && placed < budget; i++) {
    const s = shown[i];
    if (s === undefined || !s.vis) continue;
    if (s.fog < FOG_TO_NAME) continue;
    /* A name the reader has actually been to is written whatever else says otherwise. */
    const certain = s.fog > FOG_CERTAIN;
    if (!reveal && !state.namesOutOfReach && state.phase === 'map' && !state.reachable(s.node)) continue;
    if (!certain && zoom < state.floor) continue;
    if (!certain && s.node.w < minWeight && zoom < zooms.ignoreWeightAbove) continue;

    const size = labelFontSize(s.node, zoom, weights);
    ctx.font = `${s.node.w >= weights.bold ? '500 ' : '400 '}${size + FONT_BUMP}px Spectral, Georgia, serif`;
    const pad = s.node.id === state.focus ? SELECTED_PAD : 0;
    const lx = s.sx + (s.hit || s.r) + LABEL_GAP + pad;
    const width = ctx.measureText(s.node.name).width;
    const y0 = s.sy - size * BOX_HALF;
    const y1 = s.sy + size * BOX_HALF;
    if (lx + width > W - EDGE || lx < EDGE) continue;
    if (space.blocked({ x0: lx, y0, x1: lx + width, y1 })) continue;
    /* THE CLAIM HAPPENS BEFORE THE GLYPH TEST, so a label rejected for lying across a disc has
       already taken the space and nothing else may use it. Theirs does this and it is kept: the
       alternative is a second label placed exactly where one was just refused, which reads as the
       renderer having changed its mind. */
    if (!space.claim({ x0: lx, y0, x1: lx + width, y1 })) continue;
    if (overGlyph(glyphs, s.node.id, lx, y0, lx + width, y1)) continue;

    const c0 = Math.floor((lx - CELL_PAD) / CELL_W);
    const c1 = Math.floor((lx + width + CELL_PAD) / CELL_W);
    const row = Math.round(s.sy / CELL_H);
    let clash = false;
    for (let c = c0; c <= c1 && !clash; c++) if (occupied.has(`${c},${row}`)) clash = true;
    if (clash) continue;
    for (let c = c0; c <= c1; c++) occupied.add(`${c},${row}`);

    ctx.fillStyle = `rgba(226,234,250,${(s.node.verified ? ALPHA_VERIFIED : ALPHA_UNVERIFIED) * s.a})`;
    ctx.fillText(s.node.name, lx, s.sy + BASELINE_NUDGE);
    placed += 1;
  }
  return placed;
}
